'use client'

import { preconnect } from 'react-dom'
import React from 'react'
import Image from 'next/image'
import {
  MediaController,
  MediaPlayButton,
  MediaLoadingIndicator
} from 'media-chrome/react'

import MediaSettingsMenuComponent from '@/components/Player/Settings/index'
import MediaAudioSettingsMenuComponent from '@/components/Player/Settings/audio'
import MediaPosterComponent from '@/components/Player/Poster/index'
import MediaControllBarComponent from '@/components/Player/ControlBar/index'
import { i18Config } from '@/config/playerLang'
import NetworkStatusIndicator from '@/components/networkStatusIndicator.js'
import OverlayForm from './Form'

export default function MediaChromePlayer ({
  children,
  ...props
}) {
  const controllerRef = React.useRef(null)
  const [browserLang, setLanguage] = React.useState('en')

  // set the chrome styles
  const chromeStyles = {
    '--media-base-color': props?.playerColor || 'rgb(115 103 237 / 1)',
    '--media-base-hover-background': props?.playerColor || 'rgb(115 103 237 / 1)',
    '--media-icon-color': 'white',
    '--media-text-color': 'white',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    aspectRatio: props?.width && props?.height ? props.width + '/' + props.height : '',
    maxWidth: props?.width && props?.height ? (props.width * 100 / props.height).toFixed(2) + 'vh' : ''
  }

  let poster
  if ((props?.width || props?.height) && props?.poster) {
    const parsedUrl = new URL(props?.poster)
    parsedUrl.searchParams.set('ar', `${props.width}:${props.height}`)
    parsedUrl.searchParams.set('mode', 'crop')
    poster = parsedUrl.toString()
  } else {
    poster = props?.poster
  }

  const overlayStyles = {
    width: '100%',
    height: '100%',
    position: 'relative',
    maxWidth: (props.width * 100 / props.height).toFixed(2) + 'vh',
    maxHeight: (props.height * 100 / props.width).toFixed(2) + 'vw'
  }
  const mediaOverlayStyles = {
    justifyContent: 'center',
    alignItems: 'start'
  }

  if (!props.isToolsPage && !props.isVideoCenterAligned) {
    chromeStyles.maxHeight = props?.width && props?.height ? (props.height * 100 / props.width).toFixed(2) + 'vw' : ''
  } else {
    // delete marginLeft from overlayStyles
    delete mediaOverlayStyles.justifyContent
    overlayStyles.top = '50%'
    overlayStyles.left = '50%'
    overlayStyles.transform = 'translate(-50%, -50%)'
  }

  // overlay quality loader style
  const overlayQualityLoaderStyle = {
    position: 'absolute',
    width: '100%',
    aspectRatio: props.width + '/' + props.height,
    maxWidth: (props.width * 100 / props.height).toFixed(2) + 'vh',
    maxHeight: (props.height * 100 / props.width).toFixed(2) + 'vw',
    left: '50%',
    transform: 'translate(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.1)', // light transparent overlay
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    display: 'none',
    zIndex: 10,
    borderRadius: '8px' // optional: smooth edges
  }

  const adContainerStyles = {
    width: '100%',
    maxWidth: (props.width * 100 / props.height).toFixed(2) + 'vh',
    marginLeft: '50%',
    transform: 'translate(-50%)',
    backgroundColor: 'transparent',
    display: 'none'
  }

  // check the audio only mode
  if (props.audioOnly) {
    chromeStyles.backgroundImage = `url(${poster})`
    chromeStyles.backgroundSize = 'cover'
    chromeStyles.backgroundPosition = 'center'
    chromeStyles.backgroundRepeat = 'no-repeat'
  } else if (props.isVideoCenterAligned) {
    chromeStyles.background = 'transparent'
  }

  // check the video is landscape or portrait
  let landscape = props.width > props.height

  if (Object.hasOwn(props, 'forceLandscape') && props.forceLandscape) {
    landscape = true
  }

  // handle the double click event on video to fullscreen
  const handleDoubleClick = () => {
    if (!document.fullscreenElement) {
      controllerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`)
      })
    } else {
      document.exitFullscreen()
    }
  }

  preconnect('https://video.gumlet.io', { crossOrigin: 'anonymous' })
  preconnect('https://ingest.gumlytics.com', { crossOrigin: 'anonymous' })

  React.useEffect(() => {
    // Preconnect to external domains

    const videoElement = controllerRef.current

    if (videoElement) {
      videoElement.addEventListener('dblclick', handleDoubleClick)
    }

    // Clean up event listener on component unmount
    return () => {
      if (videoElement) {
        videoElement.removeEventListener('dblclick', handleDoubleClick)
      }
    }
  }, [])

  let selectedI18Config
  // useEffect to handle the language support in the player of the browser
  React.useEffect(() => {
    // get the navigator language
    const lang = navigator.language || navigator.userLanguage

    // check the language is supported or not
    if (lang) {
      const browserLangCode = lang.split('-')[0]
      if (i18Config[browserLangCode]) {
        setLanguage(i18Config[browserLangCode])
      } else {
        setLanguage(i18Config.en)
      }
    }
  }, [])

  // adding language support to the props
  props.language = browserLang

  if (props.appOnlyPlayback && props.widevinelicenseuri && props.src) {
    const payload = {
      playbackURL: props.src,
      playbackURLType: props.src.includes('m3u8') ? 'm3u8' : 'mpd',
      licenseServer: props.widevinelicenseuri
    }
    const payloadString = JSON.stringify(payload)
    const payloadBase64 = btoa(payloadString)
    const redirectURL = `intent://player/${payloadBase64}#Intent;scheme=gumlet;package=com.gumletplayer;end`
    return (
      <div className='fatal-error-overlay' style={{ display: 'flex' }}>
        <div className='error'>
          <div className='flex flex-row items-center gap-x-4'>
            <div className='border-r-[1px] pe-4'>
              <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={0.5} stroke='currentColor' className='h-14 w-14'>
                <path strokeLinecap='round' strokeLinejoin='round' d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z' />
              </svg>
            </div>
            <div>
              <h2 className='fatal-error-heading'>Your browser doesn't support playing secure videos.</h2>
              <p className='fatal-error-type text-gray-500'>You can try our application.</p>
              <p className='fatal-error-message text-gray-500'><div class='pt-1'><a href={redirectURL} class='bg-gray-900 dark:text-gray-900 text-white dark:bg-gray-100 rounded text-sm px-4 py-1'>Open App</a></div></p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const isAudioSettings = props.controls?.audioSettings || false

  return (
    <>
      <div className='media-overlay relative isolate' style={mediaOverlayStyles}>
        <div className='image-container relative' style={overlayStyles}>
          <Image
            src={poster} // Ensure this is the correct poster URL
            alt='Media Poster'
            fill
            priority // Ensures the image loads quickly
            sizes='100vw'
            style={{
              objectFit: 'cover'
            }}
          />
          {props.isBackgroundOnly
            ? <></>
            : (
              <div role='status' className='loading-spinner absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'>
                <svg
                  aria-hidden='true'
                  className='w-8 h-8 text-gray-200 animate-spin fill-gray-800'
                  viewBox='0 0 100 101'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                    fill='currentColor'
                  />
                  <path
                    d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                    fill='currentFill'
                  />
                </svg>
                <span className='sr-only'>Loading...</span>
              </div>
              )}
        </div>
      </div>

      <div className='quality-switcher-overlay' style={overlayQualityLoaderStyle}>
        <div role='status' style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <svg
            aria-hidden='true'
            className='w-8 h-8 text-gray-200 animate-spin fill-gray-800'
            viewBox='0 0 100 101'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
              fill='currentColor'
            />
            <path
              d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
              fill='currentFill'
            />
          </svg>
          <span className='sr-only'>Loading...</span>
        </div>
      </div>

      <div className='fatal-error-overlay'>
        <div className='error'>
          <div className='flex flex-row items-center gap-x-4'>
            <div className='border-r-[1px] pe-4'>
              <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={0.5} stroke='currentColor' className='h-14 w-14'>
                <path strokeLinecap='round' strokeLinejoin='round' d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z' />
              </svg>
            </div>
            <div>
              <h2 className='fatal-error-heading'>Oops! Something has gone wrong</h2>
              <p className='fatal-error-type text-gray-500' />
              <p className='fatal-error-message text-gray-500' />
            </div>
          </div>
        </div>
      </div>

      {props.hasVastTag &&
        <div className='ad-container' style={adContainerStyles} />}
      <div className='player-form-overlay'>
        {props?.widgetSettings?.map((widget) => {
          if (widget.action === 'submit') {
            return (
              <OverlayForm
                key={widget.id}
                {...widget}
              />
            )
          } else {
            return null
          }
        })}
      </div>
      <MediaController
        ref={controllerRef}
        className='mediachrome-container'
        style={chromeStyles}
        defaultSubtitles={props.captionEnabledOnLoad}
        autohide={props.controls.neverHideControls ? '-1' : '1'}
      >
        {children}
        {/* // media poster */}
        {!props.audioOnly
          ? <MediaPosterComponent
              {...props}
            />
          : <></>}
        {/* end of poster image div */}

        <NetworkStatusIndicator lang={browserLang} />
        
        {isAudioSettings && <MediaAudioSettingsMenuComponent />}

        {/* // Start Setting Menu items */}
        <MediaSettingsMenuComponent
          // pass the props tot this component
          {...props}
        />
        {/* end of setting menu items */}

        {/* start media loading indicator   */}
        <MediaLoadingIndicator loadingdelay='1000' slot='centered-chrome' />

        {/* // center media play button */}
        {
          props.controls?.playLarge &&
            <div slot='centered-chrome' id='center-button'>
              {/* // relative rounded with bg-indigo-600 p[lace at center of the video */}
              <MediaPlayButton>
                {/* <span slot="play">
                {getMediaControlBarIcon("MediaPlayCenterButton")}
              </span>
              <span slot="pause">
                {getMediaControlBarIcon("MediaPauseCenterButton")}
              </span> */}
              </MediaPlayButton>
            </div>
        }
        {/* end of media play button div */}

        {/* // Start Media Controll bar with all the buttons */}
        <MediaControllBarComponent
          isLandscapeVideo={landscape}
          {...props}
        />
        {/* end of media controll bar */}
        <span slot='i18n'>{selectedI18Config}</span>
      </MediaController>
    </>
  )
}
